import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NearULogo from '../components/NearULogo';
import ThemeToggle from '../components/ThemeToggle';
import { Eye, EyeOff, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    dateOfBirth: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      // Default to dark if not explicitly set to light
      setIsDark(saved !== 'light');
    };
    checkTheme();
    // Listen for theme changes
    window.addEventListener('storage', checkTheme);
    const interval = setInterval(checkTheme, 100);
    return () => {
      window.removeEventListener('storage', checkTheme);
      clearInterval(interval);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (isLogin) {
      // Login
      try {
        const response = await fetch(`${API}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });
        const data = await response.json();
        
        if (!response.ok) {
          setError(data.detail || 'Login failed');
          return;
        }
        
        localStorage.setItem('nearU_loggedIn', 'true');
        if (data.access_token) {
          localStorage.setItem('nearU_auth_token', data.access_token);
        }
        navigate('/discover');
      } catch (err) {
        setError('Unable to connect. Please try again.');
      }
    } else {
      // Register
      try {
        const response = await fetch(`${API}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            date_of_birth: formData.dateOfBirth
          })
        });
        const data = await response.json();
        
        if (!response.ok) {
          setError(data.detail || 'Registration failed');
          return;
        }
        
        localStorage.setItem('nearU_loggedIn', 'true');
        if (data.access_token) {
          localStorage.setItem('nearU_auth_token', data.access_token);
        }
        navigate('/verify');
      } catch (err) {
        setError('Unable to connect. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Theme-aware colors
  const colors = {
    background: isDark 
      ? 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)'
      : 'linear-gradient(180deg, #FDFCFA 0%, #F5F2ED 100%)',
    cardBg: isDark 
      ? 'linear-gradient(145deg, rgba(28, 28, 28, 0.9), rgba(13, 13, 13, 0.95))'
      : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(253, 252, 250, 0.98))',
    cardBorder: isDark 
      ? 'rgba(212, 175, 55, 0.4)'
      : 'rgba(139, 105, 20, 0.25)',
    cardShadow: isDark 
      ? '0 16px 48px rgba(0, 0, 0, 0.8), 0 0 32px rgba(212, 175, 55, 0.2), inset 0 2px 4px rgba(212, 175, 55, 0.15)'
      : '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 16px rgba(139, 105, 20, 0.06)',
    text: isDark ? '#F4E7C3' : '#1A1410',
    textMuted: isDark ? '#F4E7C3' : '#4A4035',
    featureBg: isDark 
      ? 'linear-gradient(145deg, rgba(212, 175, 55, 0.12), rgba(212, 175, 55, 0.05))'
      : 'linear-gradient(145deg, rgba(139, 105, 20, 0.06), rgba(139, 105, 20, 0.02))',
    featureBorder: isDark ? 'rgba(212, 175, 55, 0.3)' : 'rgba(139, 105, 20, 0.2)',
    featureShadow: isDark 
      ? '0 6px 24px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(212, 175, 55, 0.3)'
      : '0 4px 16px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    featureText: isDark ? '#F4E7C3' : '#4A4035',
    copyright: isDark ? 'rgba(212, 175, 55, 0.5)' : 'rgba(74, 64, 53, 0.5)',
    particleOpacity: isDark ? 0.15 : 0.06
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.background,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '20px 16px 20px 16px',
      overflowY: 'auto',
      position: 'relative',
      transition: 'background 0.4s ease'
    }}>
      <ThemeToggle />
      {/* Animated Gold Particles Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        opacity: colors.particleOpacity,
        pointerEvents: 'none'
      }}>
        {[...Array(20)].map((_, i) => (
          <Sparkles
            key={i}
            size={Math.random() * 15 + 8}
            style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              color: isDark ? '#D4AF37' : '#8B6914',
              animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* LOGO SECTION - ULTRA LUXURY */}
      <div style={{ 
        marginBottom: '24px', 
        marginTop: '10px',
        textAlign: 'center',
        width: '100%',
        maxWidth: '450px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          marginBottom: '16px',
          padding: '24px 20px',
          background: colors.cardBg,
          backdropFilter: 'blur(30px)',
          borderRadius: '24px',
          border: `2px solid ${colors.cardBorder}`,
          boxShadow: colors.cardShadow,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.4s ease'
        }}>
          {/* Gold Shine Effect */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'linear-gradient(45deg, transparent, rgba(212, 175, 55, 0.1), transparent)',
            animation: 'shine 3s infinite',
            transform: 'rotate(45deg)'
          }} />
          
          <img 
            src="https://customer-assets.emergentagent.com/job_socialux-app/artifacts/9izqox5r_IMG_1572.jpg"
            alt="Near U"
            style={{
              height: '100px',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto',
              filter: 'drop-shadow(0 0 16px rgba(212, 175, 55, 0.4))',
              position: 'relative',
              zIndex: 2
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/nearu-logo.png';
            }}
          />
        </div>
        <p style={{ 
          color: colors.textMuted, 
          marginTop: '12px', 
          fontSize: '16px', 
          letterSpacing: '3px',
          fontWeight: '300',
          textTransform: 'uppercase',
          fontFamily: 'Cinzel, serif',
          textShadow: isDark ? '0 0 16px rgba(212, 175, 55, 0.5)' : 'none',
          transition: 'color 0.4s ease'
        }}>
          Luxury Network
        </p>
      </div>

      {/* Elite Features - 3D Cards */}
      <div style={{ 
        width: '100%', 
        maxWidth: '550px', 
        marginBottom: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        position: 'relative',
        zIndex: 1
      }}>
        {[
          { title: 'Elite', desc: 'Connect with verified luxury members' },
          { title: 'Exclusive', desc: 'Access VIP tables and premium experiences' },
          { title: 'Private', desc: 'Complete privacy control' }
        ].map((feature, idx) => (
          <div key={idx} style={{ 
            textAlign: 'center',
            padding: '14px 10px',
            background: colors.featureBg,
            backdropFilter: 'blur(20px)',
            borderRadius: '14px',
            border: `1.5px solid ${colors.featureBorder}`,
            boxShadow: colors.featureShadow,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}>
            <div style={{ 
              fontSize: '22px', 
              fontWeight: 'bold', 
              fontFamily: 'Cinzel, serif',
              background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '8px'
            }}>
              {feature.title}
            </div>
            <div style={{ 
              fontSize: '10px', 
              color: colors.featureText,
              lineHeight: '1.4',
              letterSpacing: '0.3px',
              transition: 'color 0.4s ease'
            }}>
              {feature.desc}
            </div>
          </div>
        ))}
      </div>

      {/* Login Form - Glass Morphism 3D */}
      <div style={{
        width: '100%',
        maxWidth: '450px',
        background: colors.cardBg,
        backdropFilter: 'blur(30px)',
        borderRadius: '24px',
        padding: '28px 24px',
        border: `2px solid ${colors.cardBorder}`,
        boxShadow: colors.cardShadow,
        position: 'relative',
        zIndex: 1,
        transition: 'all 0.4s ease'
      }}>
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            fontFamily: 'Cinzel, Playfair Display, serif',
            background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '8px'
          }}>
            {isLogin ? 'Welcome Back' : 'Join Near U'}
          </h2>
          <p style={{ color: colors.textMuted, fontSize: '14px', letterSpacing: '0.5px', transition: 'color 0.4s ease' }}>
            {isLogin ? 'Sign in to continue' : 'Create your account'}
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '10px',
            color: '#EF4444',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: isDark ? '#D4AF37' : '#8B6914',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  className="input-field"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: isDark ? '#D4AF37' : '#8B6914',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  className="input-field"
                  value={formData.dateOfBirth || ''}
                  onChange={handleChange}
                  required={!isLogin}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  data-testid="dob-input"
                  style={{
                    colorScheme: isDark ? 'dark' : 'light'
                  }}
                />
                <p style={{ 
                  fontSize: '11px', 
                  color: colors.textMuted, 
                  marginTop: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  You must be 18 years or older to join NearU
                </p>
              </div>
            </>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '12px', 
              fontWeight: '600', 
              color: isDark ? '#D4AF37' : '#8B6914',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Email
            </label>
            <input
              type="email"
              name="email"
              className="input-field"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="email-input"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '12px', 
              fontWeight: '600', 
              color: isDark ? '#D4AF37' : '#8B6914',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="input-field"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: isDark ? '#D4AF37' : '#8B6914',
                  cursor: 'pointer',
                  padding: '4px',
                  filter: 'drop-shadow(0 0 4px rgba(212, 175, 55, 0.4))'
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {isLogin && (
            <div style={{ textAlign: 'right', marginBottom: '28px' }}>
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  color: isDark ? '#D4AF37' : '#8B6914',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  letterSpacing: '0.5px'
                }}
              >
                Forgot Password?
              </button>
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            style={{ width: '100%', padding: '18px', fontSize: '16px' }}
            data-testid="submit-btn"
          >
            {isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div style={{ marginTop: '28px', textAlign: 'center' }}>
          <p style={{ color: colors.textMuted, fontSize: '14px', transition: 'color 0.4s ease' }}>
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            {' '}
            <button
              onClick={() => setIsLogin(!isLogin)}
              style={{
                background: 'none',
                border: 'none',
                color: isDark ? '#D4AF37' : '#8B6914',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'underline',
                letterSpacing: '0.5px'
              }}
              data-testid="toggle-auth-btn"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>

        {!isLogin && (
          <div style={{ 
            marginTop: '28px', 
            padding: '20px', 
            background: isDark ? 'rgba(212, 175, 55, 0.08)' : 'rgba(212, 175, 55, 0.06)', 
            backdropFilter: 'blur(10px)',
            borderRadius: '16px', 
            border: `1px solid ${colors.cardBorder}`,
            boxShadow: 'inset 0 1px 0 rgba(212, 175, 55, 0.2)',
            transition: 'all 0.4s ease'
          }}>
            <p style={{ fontSize: '12px', color: colors.textMuted, lineHeight: '1.6', letterSpacing: '0.3px', transition: 'color 0.4s ease' }}>
              By creating an account, you agree to our{' '}
              <a href="/terms" style={{ color: colors.accent, textDecoration: 'none' }}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" style={{ color: colors.accent, textDecoration: 'none' }}>Privacy Policy</a>
              . You must be 18+ to join Near U.
            </p>
          </div>
        )}
      </div>

      {/* Footer Links */}
      <div style={{ 
        marginTop: '32px', 
        textAlign: 'center', 
        position: 'relative', 
        zIndex: 1 
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '24px', 
          marginBottom: '16px',
          flexWrap: 'wrap'
        }}>
          <a 
            href="/privacy" 
            style={{ 
              color: colors.textMuted, 
              fontSize: '13px', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = colors.accent}
            onMouseLeave={(e) => e.target.style.color = colors.textMuted}
          >
            Privacy
          </a>
          <a 
            href="/terms" 
            style={{ 
              color: colors.textMuted, 
              fontSize: '13px', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = colors.accent}
            onMouseLeave={(e) => e.target.style.color = colors.textMuted}
          >
            Terms
          </a>
          <a 
            href="/support" 
            style={{ 
              color: colors.textMuted, 
              fontSize: '13px', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = colors.accent}
            onMouseLeave={(e) => e.target.style.color = colors.textMuted}
          >
            Support
          </a>
          <a 
            href="/safety" 
            style={{ 
              color: colors.textMuted, 
              fontSize: '13px', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = colors.accent}
            onMouseLeave={(e) => e.target.style.color = colors.textMuted}
          >
            Safety
          </a>
        </div>
        <p style={{ color: colors.copyright, fontSize: '12px', letterSpacing: '1px', transition: 'color 0.4s ease' }}>
          © 2025 Near U. All rights reserved.
        </p>
      </div>

      <style>{`
        @keyframes shine {
          0% { left: -50%; }
          100% { left: 150%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;